/**
 * Señales de fiabilidad verificables (sin juzgar temas ni revistas):
 * - Retractados: no cuentan.
 * - Grupos de autores: muchos papers del mismo equipo no son evidencia independiente; cada uno
 *   pesa 1/√n (17 papers de un grupo cuentan como ~4).
 *
 * Se descartó penalizar revistas «del propio campo»: la regla que pilla a *Homeopathy* también
 * pilla a *Vaccine*, y distinguirlas exige un juicio previo sobre el tema.
 */

import type { Paper, PaperResult } from "./models";

export const RETRACTED_TYPE = "Retracted Publication";
// Papers que comparten al menos MIN_SHARED_AUTHORS autores son del mismo grupo; el ajuste se
// aplica a grupos de MIN_GROUP_PAPERS papers o más
export const MIN_SHARED_AUTHORS = 2;
export const MIN_GROUP_PAPERS = 3;

export interface Correction {
  kind: "retracted" | "author_group" | "reviews_tiebreak";
  // Explicación para el usuario (español) y sus datos, para redactarla en otro idioma
  text: string;
  params?: Record<string, string | number>;
  // Papers afectados y factor aplicado a su peso
  papers: string[];
  factor: number;
}

export function isRetracted(p: Paper): boolean {
  return /^\s*retracted\b/i.test(p.title) || (p.publication_types ?? []).some((t) => /retracted publication/i.test(t));
}

/** Clave de autor independiente del formato: "Geier MR" (Europe PMC) y "Mark R. Geier" → "geier m". */
export function authorKey(name: string): string | null {
  const t = name.trim().replace(/\.$/, "").split(/\s+/);
  if (t.length < 2) return null;
  const last = t.at(-1)!;
  if (/^[A-Z]{1,3}$/.test(last)) return `${t.slice(0, -1).join(" ").toLowerCase()} ${last[0]!.toLowerCase()}`;
  return `${last.toLowerCase()} ${t[0]![0]!.toLowerCase()}`;
}

export function authorKeys(p: Paper): Set<string> {
  return new Set(
    (p.authors ?? "")
      .split(/,\s*/)
      .filter((a) => a && !/et al/i.test(a))
      .map(authorKey)
      .filter((x): x is string => !!x),
  );
}

/** Grupos (índices) de papers que comparten ≥ MIN_SHARED_AUTHORS autores, directa o indirectamente. */
export function authorGroups(papers: Paper[]): number[][] {
  const parent = papers.map((_, i) => i);
  const find = (i: number): number => (parent[i] === i ? i : (parent[i] = find(parent[i]!)));
  const sets = papers.map(authorKeys);
  // Índice autor → papers, para no comparar todos con todos
  const byAuthor = new Map<string, number[]>();
  sets.forEach((s, i) => s.forEach((a) => byAuthor.set(a, [...(byAuthor.get(a) ?? []), i])));
  const shared = new Map<string, number>();
  for (const idx of byAuthor.values()) {
    for (let x = 0; x < idx.length; x++) {
      for (let y = x + 1; y < idx.length; y++) {
        const key = `${idx[x]}-${idx[y]}`;
        const n = (shared.get(key) ?? 0) + 1;
        shared.set(key, n);
        if (n === MIN_SHARED_AUTHORS) parent[find(idx[x]!)] = find(idx[y]!);
      }
    }
  }
  const groups = new Map<number, number[]>();
  papers.forEach((_, i) => groups.set(find(i), [...(groups.get(find(i)) ?? []), i]));
  return [...groups.values()].filter((g) => g.length >= MIN_GROUP_PAPERS);
}

const firstAuthor = (p: Paper) => (p.authors ?? "").split(/,\s*/)[0]?.trim() || "?";

/**
 * Pesos corregidos por fiabilidad de los papers que responden a la pregunta.
 * Devuelve los resultados con el peso ajustado (copias) y las correcciones aplicadas.
 */
export function applyReliability(
  results: PaperResult[],
  isRelevant: (r: PaperResult) => boolean,
): { results: PaperResult[]; corrections: Correction[] } {
  const factor = new Map<PaperResult, number>();
  const corrections: Correction[] = [];

  const relevant = results.filter(isRelevant);
  const retracted = relevant.filter((r) => isRetracted(r.paper));
  if (retracted.length) {
    retracted.forEach((r) => factor.set(r, 0));
    corrections.push({
      kind: "retracted",
      text: `${retracted.length === 1 ? "Un paper retractado no cuenta" : `${retracted.length} papers retractados no cuentan`} en el veredicto.`,
      papers: retracted.map((r) => r.paper.source_id),
      factor: 0,
      params: { n: retracted.length },
    });
  }

  const alive = relevant.filter((r) => !factor.has(r));
  for (const group of authorGroups(alive.map((r) => r.paper))) {
    const members = group.map((i) => alive[i]!);
    const f = 1 / Math.sqrt(members.length);
    members.forEach((r) => factor.set(r, f));
    // El autor más repetido del grupo, para nombrarlo
    const counts = new Map<string, number>();
    for (const r of members) {
      for (const raw of (r.paper.authors ?? "").split(/,\s*/)) {
        const a = raw.trim().replace(/\.$/, "");
        if (a) counts.set(a, (counts.get(a) ?? 0) + 1);
      }
    }
    const lead = [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? firstAuthor(members[0]!.paper);
    corrections.push({
      kind: "author_group",
      text:
        `${members.length} papers del mismo grupo de autores (${lead} y otros) no son evidencia independiente: ` +
        `cada uno pesa ${Math.round(f * 100)} % (en conjunto, como ${Math.sqrt(members.length).toFixed(1)} papers).`,
      papers: members.map((r) => r.paper.source_id),
      factor: f,
      params: { n: members.length, lead, pct: Math.round(f * 100), eq: Math.sqrt(members.length).toFixed(1) },
    });
  }

  return {
    results: results.map((r) =>
      factor.has(r) ? { ...r, classification: { ...r.classification!, weight: r.classification!.weight * factor.get(r)! } } : r,
    ),
    corrections,
  };
}
